import { ReactElement, useEffect, useState } from 'react';
import { Controller, useForm, Control, UseFormRegister } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
  Checkbox,
  Input,
  PrimaryButton,
  SecondaryButton,
  Select,
  Typography,
  useSnackbar,
  SnackbarStatus,
  StatusBar,
} from '@weareopenr/component-library';
import { IConfiguredAddons, CONFIG_FIELD_TYPES, IConfigSchemaField } from 'src/api/getBrands/types';
import { addChannelToBrand, TAddChannelToBrandRequest } from 'src/api/addChannelToBrand';
import { updateChannel, TUpdateChannelToBrandRequest } from 'src/api/updateChannel';
import { buildAddPayload } from './buildAddPayload/buildAddPayload';
import { buildEditPayload } from './buildEditPayload/buildEditPayload';
import { toSnakeCase } from 'src/utils/toSnakeCase/toSnakeCase';
import { IFormData, IModal, TCommonFieldsKeys, TSnakeCaseKeys } from './types';
import styles from './ChannelModal.module.scss';
import { useUserTracking } from 'src/hooks';
import { URL_VALIDATION_REGEX, DANGEROUS_URL_PATTERN } from 'src/constants/regex';

type ConfigFieldProps = {
  field: IConfigSchemaField;
  control: Control<IFormData>;
  register: UseFormRegister<IFormData>;
  errorMessage?: string;
  isEdit: boolean;
};

type ControlledConfigFieldProps = Pick<ConfigFieldProps, 'field' | 'control' | 'errorMessage'>;

const BooleanConfigField = ({
  field,
  control,
  errorMessage,
}: ControlledConfigFieldProps): ReactElement => (
  <div className={styles['input']}>
    <Controller
      name={`config.${field.key}`}
      control={control}
      rules={{
        validate: field.required
          ? (value) => value === true || `Please check ${field.label.toLowerCase()}`
          : undefined,
      }}
      render={({ field: { onChange, value } }) => (
        <Checkbox
          label={field.label}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          error={errorMessage}
        />
      )}
    />
  </div>
);

const SelectConfigField = ({
  field,
  control,
  errorMessage,
}: ControlledConfigFieldProps): ReactElement => {
  const selectOptions = (field.options ?? []).map((opt) => ({ value: opt, name: opt }));
  return (
    <div className={styles['input']}>
      <Controller
        name={`config.${field.key}`}
        control={control}
        rules={{
          required: field.required ? `Please select a ${field.label.toLowerCase()}` : false,
        }}
        render={({ field: { onChange, value } }) => (
          <Select
            label={field.label}
            value={String(value ?? '')}
            onChange={onChange}
            options={selectOptions}
            error={errorMessage}
          />
        )}
      />
    </div>
  );
};

const MultilineConfigField = ({
  field,
  control,
  errorMessage,
}: ControlledConfigFieldProps): ReactElement => (
  <div className={styles['input']}>
    <Controller
      name={`config.${field.key}`}
      control={control}
      rules={{
        required: field.required ? `Please enter a ${field.label.toLowerCase()}` : false,
      }}
      render={({ field: { onChange, value } }) => (
        <Input
          id={`config-${field.key}`}
          label={field.label}
          placeholder={field.placeholder}
          value={String(value ?? '')}
          onChange={onChange}
          rows={4}
          error={errorMessage}
          type="text"
        />
      )}
    />
  </div>
);

const TextConfigField = ({
  field,
  register,
  errorMessage,
  isEdit,
}: Omit<ConfigFieldProps, 'control'>): ReactElement => {
  const inputTypeMap: Record<string, string> = { secret: 'password', number: 'number' };
  const inputType = inputTypeMap[field.type] ?? 'text';
  const isSecretInEditMode = isEdit && field.type === 'secret';
  const placeholder = isSecretInEditMode ? '*************' : field.placeholder;

  return (
    <div className={styles['input']}>
      <Input
        type={inputType}
        label={field.label}
        placeholder={placeholder}
        register={register(`config.${field.key}`, {
          required:
            field.required && !isSecretInEditMode
              ? `Please enter a ${field.label.toLowerCase()}`
              : false,
          setValueAs:
            field.type === 'number' ? (v) => (v === '' ? undefined : Number(v)) : undefined,
        })}
        error={errorMessage}
      />
    </div>
  );
};

const isValidConfigField = (field: IConfigSchemaField): boolean =>
  Boolean(field.key && field.label && CONFIG_FIELD_TYPES.includes(field.type as never));

const ConfigField = (props: ConfigFieldProps): ReactElement | null => {
  const { field, control, register, errorMessage, isEdit } = props;

  if (!isValidConfigField(field)) {
    return null;
  }

  if (field.type === 'boolean') {
    return <BooleanConfigField field={field} control={control} errorMessage={errorMessage} />;
  }

  if (field.type === 'select' && field.options) {
    return <SelectConfigField field={field} control={control} errorMessage={errorMessage} />;
  }

  if (field.type === 'multiline') {
    return <MultilineConfigField field={field} control={control} errorMessage={errorMessage} />;
  }

  return (
    <TextConfigField
      field={field}
      register={register}
      errorMessage={errorMessage}
      isEdit={isEdit}
    />
  );
};

export const ModalContent = ({
  currentBrand,
  refetch,
  closeModal,
  data,
  isFetching,
  variant,
  configurationId,
}: Omit<IModal, 'open'>): ReactElement => {
  const { showSnackbar } = useSnackbar();
  const { trackEvent, captureException } = useUserTracking();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const title = `${variant} channel`;
  const isEdit = variant === 'Edit';
  const placeholder = isEdit ? '*************' : ' ';

  const closeModalAndRefetch = () => {
    closeModal();
    refetch();
  };

  const { mutate: addChannelMutate, isLoading: isAddLoading } = useMutation(
    (variables: TAddChannelToBrandRequest) => addChannelToBrand(variables),
    {
      onSuccess: (_data, variables) => {
        const channelType = data?.find(
          (channel) => channel.addon_id === variables.addon_id,
        )?.addon_name;
        trackEvent('channel_created', {
          channel_type: channelType,
          brand_id: currentBrand.brand_id,
        });
        setErrorMessage(null);
        closeModalAndRefetch();
        showSnackbar({
          message: 'A channel has successfully been added.',
          status: SnackbarStatus.SUCCESS,
        });
      },
      onError: (e: Error) => {
        setErrorMessage(e.message);
        captureException(e);
      },
    },
  );

  const { mutate: editChannelMutate, isLoading: isEditLoading } = useMutation(
    (params: { id: string; data: TUpdateChannelToBrandRequest }) =>
      updateChannel(params.id, params.data),
    {
      onSuccess: (data) => {
        trackEvent('channel_updated', {
          channel_id: configurationId,
          brand_id: currentBrand.brand_id,
        });
        setErrorMessage(null);
        closeModalAndRefetch();
        showSnackbar({
          message: `${data.preferred_name} channel has successfully been updated.`,
          status: SnackbarStatus.SUCCESS,
        });
      },
      onError: (e: Error) => {
        setErrorMessage(e.message);
        captureException(e);
      },
    },
  );

  const defaultValues: IFormData = {
    addonId: '',
    preferredName: '',
    username: '',
    externalId: '',
    secrets: {
      password: '',
      contents: '',
      accessToken: '',
    },
    endpoints: [],
    config: {},
  };

  const {
    handleSubmit,
    formState: { errors, dirtyFields },
    watch,
    control,
    register,
    setValue,
  } = useForm<IFormData>({
    defaultValues,
    mode: 'onChange',
  });
  const channelId = watch('addonId');
  const channel = data?.find((channel) => channel.addon_id === channelId);
  const currentConfiguredAddon = currentBrand.configured_addons.find(
    (addon) => addon.configuration_id === configurationId,
  );

  const isValidUrl = (value: string): boolean | string => {
    if (!value || value.trim() === '') return true;
    if (DANGEROUS_URL_PATTERN.test(value)) {
      return 'Please enter a valid URL';
    }
    if (!URL_VALIDATION_REGEX.test(value)) {
      return 'Please enter a valid URL';
    }
    return true;
  };

  const addonEndpoints = channel?.endpoints ?? [];
  const mergedEndpoints = addonEndpoints.map((defaultEp) => {
    const configuredEp = currentConfiguredAddon?.endpoints?.find(
      (e) => e.endpoint_id === defaultEp.endpoint_id,
    );
    return {
      endpoint_id: defaultEp.endpoint_id,
      name: defaultEp.name,
      default_url: defaultEp.url,
      override_url: configuredEp?.override_url ?? null,
    };
  });

  const authenticationType = channel?.authentication_types && channel?.authentication_types[0];

  // Get config schema from the addon (marketplace data)
  const configSchema = channel?.config_schema ?? [];
  const sortedConfigSchema = [...configSchema].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const options = (data ?? []).map((channel) => ({
    value: channel.addon_id,
    name: channel.addon_name,
  }));

  const currentChannel = isEdit
    ? {
        ...currentConfiguredAddon,
        external_id: currentConfiguredAddon?.external_id ? currentConfiguredAddon?.external_id : '',
      }
    : channel;

  const currentAuthenticationType = isEdit
    ? currentConfiguredAddon?.authentication_type
    : authenticationType;

  const onPopulateFields = () => {
    const commonFieldsToPopulate = Object.keys(defaultValues).filter(
      (key) => key !== 'secrets' && key !== 'addonId' && key !== 'endpoints' && key !== 'config',
    ) as TCommonFieldsKeys[];

    commonFieldsToPopulate.forEach((key: TCommonFieldsKeys) => {
      setValue(key, `${(currentChannel as IConfiguredAddons)[toSnakeCase(key) as TSnakeCaseKeys]}`);
    });
  };

  useEffect(() => {
    if (isEdit && currentConfiguredAddon) {
      setValue('addonId', currentConfiguredAddon.addon_id);
      onPopulateFields();
    }
  }, [variant, currentConfiguredAddon]);

  useEffect(() => {
    if (channelId && !isEdit) {
      setValue('preferredName', `${currentChannel?.addon_name}`, { shouldValidate: true });
    }
  }, [channelId]);

  useEffect(() => {
    if (mergedEndpoints.length > 0) {
      const endpointFormData = mergedEndpoints.map((ep) => ({
        endpoint_id: ep.endpoint_id,
        url: ep.override_url ?? '',
      }));
      setValue('endpoints', endpointFormData);
    } else {
      setValue('endpoints', []);
    }
  }, [channelId, currentConfiguredAddon, channel?.endpoints]);

  // Populate config values
  useEffect(() => {
    if (sortedConfigSchema.length > 0) {
      const configValues: Record<string, string | number | boolean> = {};
      sortedConfigSchema.forEach((field) => {
        // Skip unknown field types - only render approved fields from CONFIG_FIELD_TYPES
        if (!field.key || !field.label || !CONFIG_FIELD_TYPES.includes(field.type)) {
          return;
        }
        // In edit mode, use saved value (but never pre-populate secrets); otherwise use default
        const isSecretField = field.type === 'secret';
        const savedValue =
          isEdit && !isSecretField ? currentConfiguredAddon?.config?.[field.key] : undefined;
        if (savedValue !== undefined) {
          configValues[field.key] = savedValue;
        } else if (field.default !== undefined && !isSecretField) {
          configValues[field.key] = field.default;
        } else if (field.type === 'boolean') {
          configValues[field.key] = false;
        } else {
          configValues[field.key] = '';
        }
      });
      setValue('config', configValues);
    } else {
      setValue('config', {});
    }
  }, [channelId, currentConfiguredAddon, channel?.config_schema]);

  const onSubmit = (data: IFormData) => {
    isEdit
      ? editChannelMutate({
          id: configurationId as string,
          data: buildEditPayload(
            data,
            currentChannel as IConfiguredAddons,
            dirtyFields as unknown as IFormData,
          ) as TUpdateChannelToBrandRequest,
        })
      : addChannelMutate(buildAddPayload(data, currentAuthenticationType, currentBrand.brand_id));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['heading-text']}>
        {title}
      </Typography>
      {!isEdit && (
        <div className={styles['input']}>
          <Controller
            name="addonId"
            rules={{
              required: 'Please select a channel',
            }}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                value={value}
                onChange={onChange}
                label="Channel"
                options={options}
                error={error?.message}
                loading={isFetching}
              />
            )}
          />
        </div>
      )}

      {currentChannel && (
        <div className={styles['input']}>
          <Input
            type="text"
            label="Preferred name"
            register={register('preferredName', {
              required: 'Please enter a name',
              validate: {
                nameIsUnique: (value) =>
                  currentBrand?.configured_addons?.some(
                    (addon: IConfiguredAddons) =>
                      !(
                        isEdit &&
                        currentConfiguredAddon?.preferred_name.toLowerCase() ===
                          addon.preferred_name.toLowerCase()
                      ) && addon.preferred_name.toLowerCase() === value.toLowerCase(),
                  )
                    ? 'A channel with this preferred name already exists'
                    : true,
              },
            })}
            error={errors?.preferredName?.message}
          />
        </div>
      )}
      {currentAuthenticationType && (
        <div className={styles['credential-container']}>
          <Typography className={styles['credential-text']} weight="semi-bold" size="medium">
            Login credentials
          </Typography>
          {currentAuthenticationType === 'BASIC' && (
            <>
              <div className={styles['input']}>
                <Input
                  type="text"
                  label="Username"
                  register={register('username', {
                    required: 'Please enter a username',
                  })}
                  error={errors?.username?.message}
                />
              </div>
              <div className={styles['input']}>
                <Input
                  type="password"
                  placeholder={placeholder}
                  label="Password"
                  register={register('secrets.password', {
                    required: !isEdit && 'Please enter a password',
                  })}
                  error={errors?.secrets?.password?.message}
                />
              </div>
            </>
          )}
          {currentAuthenticationType === 'CERTIFICATE' && (
            <div className={styles['input']}>
              <Input
                type="text"
                label="Contents"
                placeholder={placeholder}
                register={register('secrets.contents', {
                  required: !isEdit && 'Please enter contents',
                })}
                error={errors?.secrets?.contents?.message}
              />
            </div>
          )}
          {currentAuthenticationType === 'TOKEN' && (
            <div className={styles['input']}>
              <Input
                type="text"
                label="Token"
                placeholder={placeholder}
                register={register('secrets.accessToken', {
                  required: !isEdit && 'Please enter a token',
                })}
                error={errors?.secrets?.accessToken?.message}
              />
            </div>
          )}
          <Typography className={styles['credential-text']} weight="semi-bold" size="medium">
            External System
          </Typography>
          <div className={styles['input']}>
            <Input
              type="text"
              label="External ID"
              register={register('externalId')}
              error={errors?.externalId?.message}
            />
          </div>
        </div>
      )}
      {mergedEndpoints.length > 0 && (
        <>
          <Typography className={styles['credential-text']} weight="semi-bold" size="medium">
            Endpoint URLs
          </Typography>
          {mergedEndpoints.map((endpoint, index) => {
            const isRequired = !endpoint.default_url;
            return (
              <div key={endpoint.endpoint_id} className={styles['input']}>
                <Input
                  type="text"
                  label={endpoint.name}
                  placeholder={endpoint.default_url ?? 'Required - no default URL'}
                  register={register(`endpoints.${index}.url`, {
                    validate: {
                      requiredAndValidUrl: (value: string) => {
                        const trimmed = typeof value === 'string' ? value.trim() : '';
                        if (isRequired && trimmed.length === 0) {
                          return 'Please enter a URL';
                        }
                        if (trimmed.length === 0) {
                          return true;
                        }
                        return isValidUrl(trimmed);
                      },
                    },
                  })}
                  error={errors?.endpoints?.[index]?.url?.message}
                />
                <input type="hidden" {...register(`endpoints.${index}.endpoint_id`)} />
              </div>
            );
          })}
        </>
      )}

      {sortedConfigSchema.length > 0 && (
        <>
          <Typography className={styles['credential-text']} weight="semi-bold" size="medium">
            Configuration
          </Typography>
          {sortedConfigSchema.map((field) => {
            const fieldError = errors?.config?.[field.key as keyof typeof errors.config];
            const errorMessage =
              fieldError && 'message' in fieldError ? fieldError.message : undefined;

            return (
              <ConfigField
                key={field.key}
                field={field}
                control={control}
                register={register}
                errorMessage={errorMessage}
                isEdit={isEdit}
              />
            );
          })}
        </>
      )}

      <div className={styles['controls']}>
        <SecondaryButton size="full-width" onClick={closeModal}>
          Back
        </SecondaryButton>
        <PrimaryButton size="full-width" type="submit" loading={isEditLoading || isAddLoading}>
          {isEdit ? 'Continue' : 'Add'}
        </PrimaryButton>
      </div>

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}
    </form>
  );
};
